// src/Components/LivelinessDetection.js
import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, Button, Card, CardContent, CircularProgress } from "@mui/material";
import FinalScreen from "./FinalScreen";
import BotAssistant from "./BotAssistant";
import { LivelinessService } from "../Services/LivelinessDetectionService";
import { SessionStore } from "../Services/SessionStore";
import { auditLogService } from "../Services/AuditLogService";
import { useSelector } from "react-redux";

const LivelinessDetection = ({ onComplete }) => {
  const liveVideoRef = useRef(null);
  const playbackRef = useRef(null);
  
  const [hasPermission, setHasPermission] = useState(false);
  const [error, setError] = useState("");
  const [recordedVideo, setRecordedVideo] = useState(null);
  const [currentRecordedBlob, setCurrentRecordedBlob] = useState(null); 
  const [mediaStream, setMediaStream] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [phase, setPhase] = useState('countdown'); // 'countdown', 'recording', 'waiting'
  const [showContinue, setShowContinue] = useState(false);
  const timerRef = useRef(null);
  const customerSession = localStorage.getItem("session");
  
  // new state to store all recorded blobs
  const [recordedBlobs, setRecordedBlobs] = useState([]);
  const [needsUserStart, setNeedsUserStart] = useState(false);

  // step tracking
  const [step, setStep] = useState(1);
  const totalSteps = 3;

  const stepAudios = {
    1: process.env.PUBLIC_URL + "/botVoice/VO3.mp4",
    2: process.env.PUBLIC_URL + "/botVoice/VO4.mp4",
    3: process.env.PUBLIC_URL + "/botVoice/VO5.mp4",
  };

  // Enable camera (video only)
  const enableCamera = async () => {
    try {
      console.log('🎥 LivelinessDetection: Starting camera initialization');
      setError("");
      
      console.log('🎥 LivelinessDetection: Requesting camera access...');
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: false });
      console.log('🎥 LivelinessDetection: ✅ Stream obtained:', stream);
      console.log('🎥 LivelinessDetection: Stream tracks:', stream.getTracks().length);
      
      setMediaStream(stream);
      console.log('🎥 LivelinessDetection: MediaStream state set');
      
      setHasPermission(true);
      setCameraReady(true);
      console.log('🎥 LivelinessDetection: ✅ Camera setup complete - stream ready');
    } catch (err) {
      console.error('🎥 LivelinessDetection: ❌ Camera setup failed:', err);
      console.error('🎥 LivelinessDetection: Error name:', err.name);
      console.error('🎥 LivelinessDetection: Error message:', err.message);
      setError(`Camera access failed: ${err.message}`);
      setHasPermission(false);
      setCameraReady(false);
    }
  };

  // Start countdown timer
  const startCountdownTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    
    setCountdown(5);
    setPhase('countdown');
    let current = 5;
    
    timerRef.current = setInterval(() => {
      current -= 1;
      setCountdown(current);
      
      if (current <= 0) {
        clearInterval(timerRef.current);
        timerRef.current = null;
        startRecording();
      }
    }, 1000);
  };

  // Start 5s recording
  const startRecording = () => {
    console.log('🎥 LivelinessDetection: === STARTING RECORDING ===');
    console.log('🎥 LivelinessDetection: startRecording called for step:', step);
    console.log('🎥 LivelinessDetection: mediaStream state:', !!mediaStream);
    console.log('🎥 LivelinessDetection: Video element exists:', !!liveVideoRef.current);
    
    if (liveVideoRef.current) {
      console.log('🎥 LivelinessDetection: Video element properties:');
      console.log('🎥 LivelinessDetection: - paused:', liveVideoRef.current.paused);
      console.log('🎥 LivelinessDetection: - readyState:', liveVideoRef.current.readyState);
      console.log('🎥 LivelinessDetection: - videoWidth:', liveVideoRef.current.videoWidth);
      console.log('🎥 LivelinessDetection: - videoHeight:', liveVideoRef.current.videoHeight);
    }
    
    // Get stream directly from video element
    const stream = liveVideoRef.current?.srcObject;
    console.log('🎥 LivelinessDetection: Video element stream exists:', !!stream);
    
    if (stream) {
      console.log('🎥 LivelinessDetection: Stream details:');
      console.log('🎥 LivelinessDetection: - Stream tracks:', stream.getTracks().length);
      console.log('🎥 LivelinessDetection: - Video tracks:', stream.getVideoTracks().length);
      stream.getVideoTracks().forEach((track, i) => {
        console.log(`🎥 LivelinessDetection: - Track ${i}: enabled=${track.enabled}, readyState=${track.readyState}`);
      });
    }
    
    if (!stream) {
      console.error('🎥 LivelinessDetection: ❌ No media stream available from video element');
      console.error('🎥 LivelinessDetection: Debugging info:');
      console.error('🎥 LivelinessDetection: - hasPermission:', hasPermission);
      console.error('🎥 LivelinessDetection: - cameraReady:', cameraReady);
      console.error('🎥 LivelinessDetection: - mediaStream:', !!mediaStream);
      return;
    }

    let recorder;
    try {
      recorder = new MediaRecorder(stream, { mimeType: "video/webm" });
      console.log('MediaRecorder created successfully');
    } catch (err) {
      console.error("❌ MediaRecorder error:", err);
      return;
    }

    let chunks = [];
    setIsRecording(true);
    setPhase('recording');
    console.log('Recording started for step:', step);

    recorder.ondataavailable = (e) => {
      console.log('Data available, size:', e.data.size);
      if (e.data.size > 0) chunks.push(e.data);
    };

    recorder.onstop = () => {
      console.log('Recording stopped, chunks:', chunks.length);
      const blob = new Blob(chunks, { type: "video/webm" });
      console.log('Blob created for step', step, '- size:', blob.size, 'bytes');
      console.log('Blob type:', blob.type);
      
      setRecordedBlobs(prev => {
        const newBlobs = [...prev, blob];
        console.log('=== VIDEO STORAGE UPDATE ===');
        console.log('Step', step, 'video added to storage');
        console.log('Total recorded blobs:', newBlobs.length);
        console.log('Blob sizes:', newBlobs.map((b, i) => `Video ${i+1}: ${b.size} bytes`));
        console.log('All blobs valid:', newBlobs.every(b => b.size > 0));
        console.log('=============================');
        return newBlobs;
      });
      setIsRecording(false);
      setPhase('waiting');
      
      // Auto-progress to next step after 2 seconds
      setTimeout(() => {
        setStep(currentStep => {
          console.log('Auto-progressing from step', currentStep, 'to step', currentStep + 1);
          if (currentStep < totalSteps) {
            setPhase('countdown');
            setTimeout(() => startCountdownTimer(), 100);
            return currentStep + 1;
          } else {
            // All steps completed, send to API after state update
            setTimeout(() => handleFinalSubmission(), 100);
            return currentStep;
          }
        });
      }, 2000);
    };

    recorder.start();
    console.log('MediaRecorder.start() called');

    // Stop after 5s
    setTimeout(() => {
      console.log('5 seconds elapsed, stopping recording');
      if (recorder.state === "recording") {
        recorder.stop();
        console.log('MediaRecorder.stop() called');
      } else {
        console.log('Recorder state:', recorder.state);
      }
    }, 5000);
  };
  




  const handleFinalSubmission = async () => {
    console.log('\n=== FINAL API SUBMISSION ===');
    console.log('✅ All steps completed, preparing to send videos to API');
    
    // Get current blobs from state
    setRecordedBlobs(currentBlobs => {
      console.log('Current recorded blobs:', currentBlobs.length);
      console.log('Blob sizes:', currentBlobs.map((b, i) => `Video ${i+1}: ${b.size} bytes`));
      
      // Validate we have exactly 3 videos
      if (currentBlobs.length !== 3) {
        console.error(`❌ Expected 3 videos, got ${currentBlobs.length}`);
        setError(`Missing videos. Expected 3, got ${currentBlobs.length}. Please try again.`);
        return currentBlobs;
      }
      
      // Validate all videos have content
      const invalidVideos = currentBlobs.filter((blob, idx) => !blob || blob.size === 0);
      if (invalidVideos.length > 0) {
        console.error('❌ Some videos are empty or invalid');
        setError('Some videos failed to record properly. Please try again.');
        return currentBlobs;
      }
      
      // Proceed with API call
      submitToAPI(currentBlobs);
      return currentBlobs;
    });
  };
  
  const submitToAPI = async (blobs) => {
    console.log('Total videos to send:', blobs.length);
    
    setIsUploading(true);
    try {
      const storedSessionId = SessionStore.getSessionId();
      if (!storedSessionId) {
        console.error('❌ No session ID found!');
        setError('Session ID not found. Please restart the KYC process.');
        return;
      }
      
      const requestId = `request_${Math.random().toString(36).slice(2, 10)}_${Date.now()}`;
      const result = await LivelinessService.detectLiveliness(blobs, storedSessionId, requestId, customerSession);
      
      console.log('✅ Face Liveliness API call successful!');
      console.log('API response:', result);
    
    } catch (err) {
      console.error('❌ Liveliness detection API failed:', err);
      setError("Liveliness detection failed. Please try again.");
      
    } finally {
      setIsUploading(false);
      setStep(totalSteps + 1);
    }
  };

  useEffect(() => {
    console.log('🚀 LivelinessDetection: Component mounted, initializing...');
    console.log('🚀 LivelinessDetection: Navigator available:', !!navigator.mediaDevices);
    console.log('🚀 LivelinessDetection: getUserMedia available:', !!navigator.mediaDevices?.getUserMedia);
    
    // Only initialize camera, don't start countdown automatically
    enableCamera();

    return () => {
      console.log('🚀 LivelinessDetection: Component unmounting, cleaning up...');
      if (timerRef.current) clearInterval(timerRef.current);
      if (liveVideoRef.current && liveVideoRef.current.srcObject) {
        liveVideoRef.current.srcObject.getTracks().forEach((t) => t.stop());
        liveVideoRef.current.srcObject = null;
      }
    };
  }, []);

  // Apply stream to video element when both are available
  useEffect(() => {
    if (mediaStream && liveVideoRef.current && !liveVideoRef.current.srcObject) {
      console.log('🎥 LivelinessDetection: Applying stored stream to video element');
      liveVideoRef.current.srcObject = mediaStream;
      liveVideoRef.current.muted = true;
      liveVideoRef.current.playsInline = true;
      liveVideoRef.current.onloadedmetadata = () => {
        console.log('🎥 LivelinessDetection: Video metadata loaded, starting countdown automatically');
        setTimeout(() => startCountdownTimer(), 2000);
      };
      console.log('🎥 LivelinessDetection: Stream applied to video element');
    }
  }, [mediaStream, liveVideoRef.current]);

  // Auto-start countdown when camera is ready
  useEffect(() => {
    if (cameraReady && phase === 'countdown' && step === 1) {
      console.log('🎥 LivelinessDetection: Camera ready, auto-starting countdown for step 1');
      setTimeout(() => startCountdownTimer(), 2000);
    }
  }, [cameraReady, phase, step]);

  // Show final screen after all steps are completed
  useEffect(() => {
    if (step > totalSteps && onComplete) {
      onComplete();
    }
  }, [step, onComplete]);

  if (step > totalSteps) {
    return <FinalScreen />;
  }

  return (
    <Box sx={{ display: "flex", justifyContent: "center", mt: 4, px: 2 }}>
      <Card sx={{ width: "100%", maxWidth: 1200, borderRadius: 3, p: 2 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Liveliness Detection
          </Typography>

          {/* Action counter */}
          <Typography variant="body2" color="text.primary" mb={2}>
            Action {step} of {totalSteps}
          </Typography>

          {/* Instructions */}
          <Typography variant="body2" color="text.secondary" mb={2}>
            {step === 1 && "👉 Please wave your hand in front of the camera."}
            {step === 2 && "👉 Please blink your eyes clearly."}
            {step === 3 && "👉 Please move your head left and right."}
          </Typography>

          {/* Video Preview Box */}
          <Box
            sx={{
              position: "relative",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mb: 2,
              background: "#000",
              borderRadius: 2,
              width: "100%",
              height: { xs: "260px", sm: "300px", md: "380px", lg: "450px" },
              maxHeight: 500,
              overflow: "hidden",
            }}
          >
            {hasPermission ? (
              <>
                <video
                  ref={liveVideoRef}
                  autoPlay
                  playsInline
                  muted
                  controls={false}
                  style={{ width: "100%", height: "100%", objectFit: "cover", transform: "scaleX(-1)" }}
                />

                {/* Recording Indicator - Hidden */}
                {/* {isRecording && (
                  <Box sx={{ position: "absolute", top: 16, left: 16, display: "flex", alignItems: "center", gap: 1, background: "rgba(255,0,0,0.8)", color: "white", px: 2, py: 1, borderRadius: 2 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: "red", animation: "blink 1s infinite", "@keyframes blink": { "0%, 50%": { opacity: 1 }, "51%, 100%": { opacity: 0.3 } } }} />
                    <Typography variant="body2" fontWeight="bold">REC</Typography>
                  </Box>
                )} */}
              </>
            ) : (
              <Typography color="error" p={2}>
                {error || "Requesting camera permission..."}
              </Typography>
            )}
          </Box>

          {/* Status Message */}
          <Box sx={{ textAlign: "center", mt: 2 }}>
            <Typography variant="h6" color="primary">
              {phase === "countdown" && `Get ready... ${countdown}s`}
              {phase === "recording" && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: 'red', animation: 'pulse 1s infinite', '@keyframes pulse': { '0%': { opacity: 1 }, '50%': { opacity: 0.5 }, '100%': { opacity: 1 } } }} />
                  {`Recording video ${step} of ${totalSteps}...`}
                </Box>
              )}
              {phase === "waiting" && step < totalSteps && "Video recorded! Moving to next step..."}
              {phase === "waiting" && step === totalSteps && "All videos recorded! Processing..."}
              {isUploading && "Submitting liveliness verification..."}
            </Typography>
            

          </Box>
        </CardContent>
      </Card>

      <BotAssistant voiceFile={stepAudios[step]} show={step <= totalSteps} />
    </Box>
  );
};

export default LivelinessDetection;